import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { Box, Button, Loading, Typography } from '@thirdspacelearning/library';
import { QUERY_STATUS } from '@tsl-frontend/tsl-utilities';

import { getProgrammes } from '@api';
import { CurriculumQueryKeys } from '@services';
import { Country } from '@types';
import { countryTabList } from '@utils';
import { ErrorActionPanel, PageLayout, TabsPanel } from '@shared';
import { ProgrammeType } from '../../types';
import ProgrammeConnectionsDiagram from './ProgrammeConnectionsDiagram';
import {
    buildProgrammeConnectionsDiagrams,
    getVisibleProgrammes,
} from './programmeConnectionsGraph';

const sx = {
    pageHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    filtersRow: {
        display: 'flex',
        gap: 1,
    },
    content: {
        height: 'calc(100vh - 250px)',
        mt: 2,
        overflow: 'auto',
    },
    diagrams: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 3,
        minWidth: '100%',
    },
    emptyState: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyStateText: {
        color: 'text.secondary',
    },
};

const ProgrammesConnectionsPage = () => {
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState<Country>(Country.GB);
    const [showOrphaned, setShowOrphaned] = useState(false);
    const [liveOnly, setLiveOnly] = useState(true);

    const { status, data: programmes } = useQuery<ProgrammeType[]>({
        queryKey: [CurriculumQueryKeys.PROGRAMMES, selectedTab],
        queryFn: () => getProgrammes(selectedTab),
        retry: 1,
        refetchOnWindowFocus: false,
    });

    const visibleProgrammes = useMemo(
        () => getVisibleProgrammes({ programmes, showOrphaned, liveOnly }),
        [programmes, showOrphaned, liveOnly],
    );

    const mermaidDiagrams = useMemo(
        () => buildProgrammeConnectionsDiagrams(visibleProgrammes),
        [visibleProgrammes],
    );

    return (
        <PageLayout title="Programme Connections">
            <Box sx={sx.pageHeader}>
                <TabsPanel<Country>
                    selectedTab={selectedTab}
                    tabList={countryTabList}
                    setSelectedTab={setSelectedTab}
                />
                <Box sx={sx.filtersRow}>
                    <Button
                        variant={liveOnly ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        onClick={() => setLiveOnly((v) => !v)}
                    >
                        Live only
                    </Button>
                    <Button
                        variant={showOrphaned ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        onClick={() => setShowOrphaned((v) => !v)}
                    >
                        Show unconnected
                    </Button>
                </Box>
            </Box>
            {status === QUERY_STATUS.pending && (
                <Loading loadingMessage="Loading programmes..." />
            )}
            {status === QUERY_STATUS.error && (
                <ErrorActionPanel
                    error="There was an error loading the programmes"
                    message="Please refresh the page and try again. If the problem persists, please let us know."
                />
            )}
            {status === QUERY_STATUS.success && (
                <Box sx={sx.content}>
                    {visibleProgrammes.length > 0 ? (
                        <Box sx={sx.diagrams}>
                            {mermaidDiagrams.map((diagram, index) => (
                                <ProgrammeConnectionsDiagram
                                    key={`programme-connections-${index}`}
                                    diagram={diagram}
                                    onProgrammeClick={(programmeId) =>
                                        navigate(`/programmes/${programmeId}`)
                                    }
                                />
                            ))}
                        </Box>
                    ) : (
                        <Box sx={sx.emptyState}>
                            <Typography sx={sx.emptyStateText}>
                                No programmes match the current filters.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </PageLayout>
    );
};

export default ProgrammesConnectionsPage;
